import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse, ServiceModuleModel } from '@/api/model'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { Button } from '@/components/shadcn/button'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterDateRangePicker } from '@/components/common/filters/FilterDateRangePicker'
import { useTimeTrackingEntries } from '../hooks/useTimeTrackingEntries'
import { TimeTrackingEntriesTable } from './TimeTrackingEntriesTable'

interface Props {
  module: ServiceModuleModel
  /** Fixed employee of this view (from the URL, `?view=employee&employeeId=…`). */
  employeeId: string
  onBack: () => void
}

/** Unwrap the query response from the axios wrapper if needed (hooks return the full response). */
function unwrap<T>(data: unknown): T | undefined {
  return ((data as { data?: T })?.data ?? data) as T | undefined
}

/**
 * Employee time-tracking page (row click in the module overview → here).
 * Like `TimeTrackingAllEntriesPage`, but the employee is fixed (no employee filter).
 * Only a date-range filter + paginated list for the employee. Mirrors
 * `AbsenceEmployeeEntriesPage`: employee id from the URL, name looked up from the
 * (cached) employee list of the module.
 */
export function TimeTrackingEmployeeEntriesPage({ module, employeeId, onBack }: Props) {
  const { t } = useTranslation(['service-modules-timetracking', 'common', 'filters'])
  const [range, setRange] = useState<{ from?: string; to?: string }>({})

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(module.id)
  const employee = useMemo(
    () => (unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? []).find((e) => e.id === employeeId),
    [employeesQuery.data, employeeId],
  )

  const entries = useTimeTrackingEntries(module.id, {
    employeeUserId: employeeId,
    from: range.from,
    to: range.to,
  })

  const name = `${employee?.firstName ?? ''} ${employee?.lastName ?? ''}`.trim() || t('dropdown.employeeFallback', { ns: 'filters' })

  return (
    <PageLayout>
      <PageHeader title={t('timetracking.employeeTitle', { name })} description={t('timetracking.employeeDescription')}>
        <PageHeaderActions>
          <Button variant="outline" className="h-9 gap-2" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            {t('buttons.back', { ns: 'common' })}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <FilterRow>
        <FilterDateRangePicker from={range.from} to={range.to} onChange={setRange} />
      </FilterRow>

      <TimeTrackingEntriesTable entries={entries} />
    </PageLayout>
  )
}
